/**
 * "Extract here" asks about each name conflict ONCE. The first round asks about the archive's
 * TOP-LEVEL names; a Replace / Merge there leads to the per-file round (files inside merged folders).
 * A top-level FILE already answered in the first round — and everything after an "Apply to all" — is
 * never asked again: its answer is carried into the per-file map.
 */
import type { ConflictResolution } from '@papan/core-ui/ui'

export interface ExtractFileConflict { zipPath: string; destPath: string; existingIsFolder: boolean }

export async function resolveExtractFileConflicts(
  fileConflicts: ReadonlyArray<ExtractFileConflict>,
  /** The first round's answers, by top-level name. */
  answered: ReadonlyMap<string, ConflictResolution>,
  /** The first round's "Apply to all", if one was chosen — applies to every remaining conflict. */
  applyToAll: ConflictResolution | null,
  ask: (c: ExtractFileConflict) => Promise<{ resolution: ConflictResolution; applyToAll: boolean }>,
): Promise<Record<string, ConflictResolution>> {
  const map: Record<string, ConflictResolution> = {}
  let all = applyToAll
  for (const c of fileConflicts) {
    const already = answered.get(c.zipPath)
    if (already) { map[c.zipPath] = already; continue }
    if (all) { map[c.zipPath] = all; continue }
    const result = await ask(c)
    map[c.zipPath] = result.resolution
    if (result.applyToAll) all = result.resolution
  }
  return map
}
